<template>
  <v-dialog v-model="visible" scrollable persistent max-width="800" @keydown.esc="cancelForm()">
    <v-card class="user-form-card">
      <v-card-title class="primary white--text py-4">
        <v-icon left color="white">{{ isNewRecord ? 'mdi-account-plus' : 'mdi-account-edit' }}</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelForm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4">
        <v-form ref="userForm" v-model="validations.isValid">
          <v-container fluid>
            <!-- Información Personal -->
            <v-row>
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-account</v-icon>
                  <span>Información Personal</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.name"
                  :rules="validations.name"
                  :error-messages="errors.name"
                  :disabled="loading"
                  label="Nombre*"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-outline"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.lastname"
                  :rules="validations.lastname"
                  :error-messages="errors.lastname"
                  :disabled="loading"
                  label="Apellidos*"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-outline"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- Información de Contacto -->
            <v-row class="mt-2">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-contact-phone</v-icon>
                  <span>Información de Contacto</span>
                </div>
              </v-col>
              <v-col cols="12" md="6" v-if="!isLimitedEdit">
                <v-text-field
                  v-model="fields.username"
                  :rules="validations.username"
                  :error-messages="errors.username"
                  :disabled="loading"
                  label="Email*"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-email-outline"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fields.phone"
                  :rules="validations.phone"
                  :error-messages="errors.phone"
                  :disabled="loading"
                  label="Teléfono*"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-phone"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- Asignaciones -->
            <v-row class="mt-2" v-if="!isLimitedEdit">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-account-key</v-icon>
                  <span>Asignaciones*</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="visibleAsignacionForm = true"
                    :disabled="loading"
                    class="ml-2"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12">
                <asignacion-form
                  v-if="visibleAsignacionForm"
                  v-model="fields.roles"
                  :available-roles="availableRoles"
                  @cancel="visibleAsignacionForm = false"
                ></asignacion-form>
                <v-card outlined class="roles-card" v-if="fields.roles.length > 0">
                  <v-list dense>
                    <template v-for="(item, index) in fields.roles">
                      <v-list-item :key="`role-item-${index}-${item}`" class="role-item" two-line>
                        <v-list-item-avatar>
                          <v-icon class="primary--text">mdi-shield-account</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium" v-text="getRoleName(item)"></v-list-item-title>
                          <v-list-item-subtitle>
                            {{ item === 'colaborator' ? 'Rol de sistema' : 'Rol de sistema' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn 
                            icon 
                            color="error" 
                            :disabled="loading || item === 'colaborator'" 
                            @click="deleteAsignacion(item)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < fields.roles.length" :key="`divider-role-${index}-${item}`"></v-divider>
                    </template>
                  </v-list>
                </v-card>
                <v-alert
                  v-else
                  type="info"
                  outlined
                  prominent
                  border="left"
                  class="mb-0"
                >
                  <div class="d-flex align-center">
                    <div>
                      <v-icon size="28" class="mr-3">mdi-information-outline</v-icon>
                    </div>
                    <div>
                      <div class="subtitle-1 font-weight-medium">No hay asignaciones</div>
                      <div class="body-2">El usuario necesita al menos un rol para guardar.</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                      outlined
                      color="primary"
                      @click="visibleAsignacionForm = true"
                    >
                      Agregar asignación
                    </v-btn>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <!-- Permisos -->
            <v-row class="mt-2" v-if="!isLimitedEdit && !isCurrentUserAdmin">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-shield-key</v-icon>
                  <span>Permisos Específicos</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="visiblePermissionsForm = true"
                    :disabled="loading"
                    class="ml-2"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Configurar
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12">
                <permissions-form
                  :visible.sync="visiblePermissionsForm"
                  v-model="fields.permissions"
                  @update:visible="visiblePermissionsForm = $event"
                ></permissions-form>
                <v-card outlined class="permissions-card" v-if="fields.permissions.length > 0">
                  <v-card-text class="pa-3">
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="(permission, index) in fields.permissions"
                        :key="index"
                        small
                        color="primary"
                        outlined
                        class="ma-1"
                      >
                        {{ getPermissionLabel(permission) }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
                <v-alert
                  v-else
                  type="info"
                  outlined
                  prominent
                  border="left"
                  class="mb-0"
                >
                  <div class="d-flex align-center">
                    <div>
                      <v-icon size="28" class="mr-3">mdi-information-outline</v-icon>
                    </div>
                    <div>
                      <div class="subtitle-1 font-weight-medium">Sin permisos específicos</div>
                      <div class="body-2">El usuario tendrá permisos basados únicamente en sus roles asignados.</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                      outlined
                      color="primary"
                      @click="visiblePermissionsForm = true"
                    >
                      Configurar permisos
                    </v-btn>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <!-- Condiciones de Vehículo (Obligatorio) -->
            <v-row class="mt-2" v-if="!isLimitedEdit && !isCurrentUserAdmin">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-car-info</v-icon>
                  <span>Condiciones de Vehículo*</span>
                </div>
              </v-col>
              <v-col cols="12">
                <v-card outlined class="pa-3">
                  <v-card-text>
                    <div class="body-2 mb-3 grey--text text--darken-1">
                      Selecciona al menos una condición de vehículo con la que puede trabajar el usuario:
                    </div>
                    <v-row>
                      <v-col v-if="DEALER_HAS_TYPE('vn')" cols="12" sm="4">
                        <v-checkbox
                          :input-value="fields.vehicleConditions.includes('vn')"
                          @change="toggleVehicleCondition('vn', $event)"
                          label="Vehículos Nuevos (VN)"
                          color="green"
                          :disabled="loading"
                          hide-details
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="green" class="mr-2" small>mdi-car-limousine</v-icon>
                              <span>Vehículos Nuevos (VN)</span>
                            </div>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col v-if="DEALER_HAS_TYPE('vo')" cols="12" sm="4">
                        <v-checkbox
                          :input-value="fields.vehicleConditions.includes('vo')"
                          @change="toggleVehicleCondition('vo', $event)"
                          label="Vehículos de Ocasión (VO)"
                          color="amber darken-2"
                          :disabled="loading"
                          hide-details
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="amber darken-2" class="mr-2" small>mdi-car-estate</v-icon>
                              <span>Vehículos de Ocasión (VO)</span>
                            </div>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col v-if="DEALER_HAS_TYPE('taller')" cols="12" sm="4">
                        <v-checkbox
                          :input-value="fields.vehicleConditions.includes('taller')"
                          @change="toggleVehicleCondition('taller', $event)"
                          label="Taller"
                          color="indigo"
                          :disabled="loading"
                          hide-details
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-2" small>mdi-tools</v-icon>
                              <span>Taller</span>
                            </div>
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="fields.vehicleConditions.length === 0"
                      type="warning"
                      dense
                      text
                      class="mt-3 mb-0"
                    >
                      <v-icon left small>mdi-alert</v-icon>
                      Debes seleccionar al menos una condición de vehículo
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-alert
          v-if="errors.username.length"
          type="error"
          dense
          text
          class="ma-0 mr-4"
        >
          {{ errors.username[0] }}
        </v-alert>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="loading"
          @click="cancelForm()"
          color="grey darken-1"
          text
          class="px-4"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          :loading="loading"
          :disabled="saveIsDisabled"
          @click="saveForm()"
          color="primary"
          class="px-4"
          depressed
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import UserService from '../../services/UserService'
// import DealerService from '../../services/DealerService'
import { mapGetters } from 'vuex'

export default {
  props: {
    value: null,
    isLimitedEdit: {
      type: Boolean,
      default: false
    },
    isAdminUser: {
      type: Boolean,
      default: false
    }
  },

  components: {
    AsignacionForm: () => import(/* webpackPrefetch: true */ './AsignacionForm'),
    PermissionsForm: () => import(/* webpackPrefetch: true */ '../../components/PermissionsForm')
  },

  data: () => ({
    visible: true,
    loading: false,
    visibleAsignacionForm: false,
    visiblePermissionsForm: false,

    // campos del formulario
    user: null,
    roles: [
      { name: 'Administrador', value: 'admin' },
      { name: 'Manager', value: 'manager' },
      { name: 'Logística', value: 'logistics' },
      { name: 'Jefe de ventas/taller', value: 'sales' },
      { name: 'Comercial', value: 'commercial' },
      { name: 'Colaborador', value: 'colaborator' },
      { name: 'Mecánico', value: 'mechanic' }
      // { name: 'Garage', value: 'garage' },
      // { name: 'Conductor', value: 'driver' }
      // { name: 'Limpieza', value: 'cleaning' }
      
    ],

    // Lista completa de permisos para mostrar labels
    allPermissions: [
      { value: 'actualizar_usuario', label: 'Actualizar usuario' },
      { value: 'agregar_usuario_normal', label: 'Agregar usuario normal' },
      { value: 'crear_conductor', label: 'Crear conductor' },
      { value: 'crear_grua', label: 'Crear grúa' },
      { value: 'crear_movimiento', label: 'Crear movimiento' },
      { value: 'crear_ubicacion', label: 'Crear ubicación' },
      { value: 'crear_vehiculo', label: 'Crear vehículo' },
      { value: 'descargar_comodato', label: 'Descargar comodato' },
      { value: 'editar_conductor', label: 'Editar conductor' },
      { value: 'editar_grua', label: 'Editar grúa' },
      { value: 'editar_movimiento', label: 'Editar movimiento' },
      { value: 'editar_ubicacion', label: 'Editar ubicación' },
      { value: 'editar_vehiculo', label: 'Editar vehículo' },
      { value: 'eliminar_conductor', label: 'Eliminar conductor' },
      { value: 'eliminar_grua', label: 'Eliminar grúa' },
      { value: 'eliminar_movimiento', label: 'Eliminar movimiento' },
      { value: 'eliminar_ubicacion', label: 'Eliminar ubicación' },
      { value: 'eliminar_usuario', label: 'Eliminar usuario' },
      { value: 'eliminar_vehiculo', label: 'Eliminar vehículo' },
      { value: 'importacion_masiva_excel', label: 'Importación masiva Excel' },
      { value: 'manage_dealer', label: 'Administrar dealer' },
      { value: 'manage_equipamiento_config', label: 'Configurar equipamiento' },
      { value: 'manage_notifications', label: 'Administrar notificaciones' },
      { value: 'manage_users', label: 'Administrar usuarios' },
      { value: 'vn', label: 'Vehículos Nuevos (VN)' },
      { value: 'vo', label: 'Vehículos Usados (VO)' },
      { value: 'taller', label: 'Taller' },
      { value: 'view_chekbox_validacion', label: 'Ver checkbox de validación' },
      { value: 'usage_chekbox_validacion', label: 'Usar checkbox de validación' }
    ],

    fields: {
      roles: [],
      permissions: [],
      vehicleConditions: [],
      username: '',
      name: '',
      lastname: '',
      phone: '',
      dealer_id: ''
    },

    // lista de usuarios (tabla Trabajador)
    userState: {
      list: [],
      search: null,
      loading: false
    },

    // validaciones
    validations: {
      isValid: true,
      phone: [
        (v) => !!v || 'Debe proporcionar un número de teléfono.',
        (v) => /^[0-9]+$/.test(v) || 'El número de teléfono solo puede contener números.'
      ],
      username: [
        (v) => !!v || 'Debe proporcionar un correo electrónico.',
        (v) => /.+@.+\..+/.test(v) || 'Debe proporcionar un correo electrónico válido.'
      ],
      name: [
        (v) => !!v || 'Debe proporcionar un nombre.'
      ],
      lastname: [
        (v) => !!v || 'Debe proporcionar al menos un apellido.'
      ]
    },

    // errores
    errors: {
      user: [],
      phone: [],
      username: [],
      name: [],
      lastname: []
    }
  }),

  async created () {
    if (this.value) {
      this.loading = true
      await this.$store.dispatch('VIEW_USER', { ...this.value }).then(result => {
        this.fields = Object.assign({}, result)
        
        // Separar los permisos de condición de vehículo del resto
        const vehicleConditionPerms = ['vn', 'vo', 'taller']
        
        // Asegurar que vehicleConditions sea siempre un array
        this.fields.vehicleConditions = Array.isArray(this.fields.permissions) 
          ? this.fields.permissions.filter(p => vehicleConditionPerms.includes(p))
          : []
          
        this.fields.permissions = Array.isArray(this.fields.permissions) 
          ? this.fields.permissions.filter(p => !vehicleConditionPerms.includes(p))
          : []
        
        this.loading = false
      })
    } else {
      this.resetFields()
    }
  },

  methods: {
    getRoleName(roleValue) {
      const role = this.roles.find(rol => rol.value === roleValue)
      return role ? role.name : roleValue
    },
    async saveForm () {
      // Limpiar errores previos antes de validar
      Object.keys(this.errors).forEach(key => { this.errors[key] = [] })
      this.$refs.userForm.validate()
      
      // Esperar un tick para que los cambios se reflejen
      await this.$nextTick()
      
      // Validar que se haya seleccionado al menos una condición de vehículo (solo para no-admins)
      if (!this.isLimitedEdit && !this.editedUserIsAdmin && this.fields.vehicleConditions.length === 0) {
        this.$toast.error('Debes seleccionar al menos una condición de vehículo (VN, VO o Taller)')
        return
      }
      
      if (this.validations.isValid) {
        this.loading = true
        if (!this.CURRENT_USER_ROLES.includes('admin')) {
          await UserService.getMe().then(({ data }) => {
            this.fields.dealer_id = data.data.dealer.id
          })
        }
        
        // Combinar permisos de vehicleConditions con permissions
        const allPermissions = [...this.fields.permissions]
        
        // Agregar permisos de condición de vehículo si no están ya incluidos
        this.fields.vehicleConditions.forEach(condition => {
          if (!allPermissions.includes(condition)) {
            allPermissions.push(condition)
          }
        })
        
        let user = Object.assign({}, this.user, this.fields, {
          permissions: allPermissions
        })
        
        if (this.isNewRecord) {
          user.lastname = this.fields.lastname
        } else {
          user.lastname = this.fields.lastname
        }
        if (this.CURRENT_USER && this.CURRENT_USER.dealer && this.CURRENT_USER.dealer.is_colaborador) {
          user.roles = Array.isArray(user.roles) ? user.roles : (Array.isArray(this.fields.roles) ? [...this.fields.roles] : [])
          if (!user.roles.includes('colaborator')) {
            user.roles.push('colaborator')
          }
        }
        this.$store.dispatch(this.isNewRecord ? 'ADD_USER' : 'UPDATE_USER', user).then(response => {
          let { error, data, status } = response
          if (error) {
            if (status === 422) {
              // mostrando los errores de validación en los campos correspondientes
              data.data.forEach(({ field, message }) => {
                // Si es el error de email duplicado, traducir
                if (field === 'username' && message && message.includes('has already been taken')) {
                  if (this.errors[field]) {
                    this.errors[field].push('El correo electrónico ya está en uso.')
                  }
                } else {
                  if (this.errors[field]) {
                    this.errors[field].push(message)
                  }
                }
              })
            }
            if (status === 400) {
              // mostrando cualquier otro error al usuario en una notificación
              this.$toast.error('El correo electrónico proporcionado ya está en uso')
            } else {
              // mostrando cualquier otro error al usuario en una notificación
              console.log(data)
              this.$toast.error('Ocurrió un error al intentar registrar o actualizar la cuenta de usuario')
            }
          } else {
            this.$toast.success(`Usuario ${this.isNewRecord ? 'creado' : 'actualizado'} correctamente`)
            this.$emit('save', { insert: this.isNewRecord, value: data })
            this.visible = false
          }
          this.loading = false
        })
      }
    },
    cancelForm () {
      this.$emit('cancel')
    },
    deleteAsignacion (item) {
      // Prevenir eliminación del rol colaborator
      if (item === 'colaborator') {
        this.$toast.warning('El rol de Colaborador no puede ser eliminado')
        return
      }
      
      this.$confirm('¿Está seguro que desea eliminar la asignación?').then(result => {
        if (result) {
          this.fields.roles = this.fields.roles.filter(el => el !== item)
          this.$toast.success('La asignación ha sido eliminada satisfactoriamente')
        }
      })
    },
    getPermissionLabel(permissionValue) {
      const permission = this.allPermissions.find(p => p.value === permissionValue)
      return permission ? permission.label : permissionValue
    },
    async toggleVehicleCondition(condition, isChecked) {
      // Crear una nueva copia del array completo
      const newConditions = [...this.fields.vehicleConditions]
      
      if (isChecked) {
        // Agregar la condición si no está ya incluida
        if (!newConditions.includes(condition)) {
          newConditions.push(condition)
        }
      } else {
        // Remover la condición si está incluida
        const index = newConditions.indexOf(condition)
        if (index > -1) {
          newConditions.splice(index, 1)
        }
      }
      
      // Recrear completamente el objeto fields para forzar reactividad
      this.fields = {
        ...this.fields,
        vehicleConditions: newConditions
      }
      
      // Esperar a que Vue procese los cambios
      await this.$nextTick()
    },
    userDebouncedSearch: _.debounce(function () {
      this.userState.loading = true
      UserService.get({ search: this.searchTerm }).then(({ data }) => {
        this.userState.list = data.items
        this.userState.loading = false
      })
    }, 1000),
    filterUsers (item, queryText, itemText) {
      return true
    },
    resetFields () {
      this.fields = {
        roles: [],
        permissions: [],
        vehicleConditions: [],
        username: '',
        name: '',
        lastname: '',
        phone: ''
      }
    }
  },

  computed: {
    ...mapGetters(['CURRENT_USER_ROLES', 'CURRENT_USER', 'DEALER_HAS_TYPE']),
    isNewRecord () {
      return !this.value
    },
    title () {
      return this.isNewRecord ? 'Nuevo usuario' : 'Modificar usuario'
    },
    // Computed específico para verificar si hay condiciones de vehículo seleccionadas
    hasValidVehicleConditions() {
      return this.fields.vehicleConditions && this.fields.vehicleConditions.length > 0
    },
    saveIsDisabled () {
      // Si el usuario editado es admin, no se requiere condición de vehículo
      const requiresVehicleCondition = !this.isCurrentUserAdmin && !this.isLimitedEdit
      
      const disabled = this.loading || 
             (!this.isLimitedEdit && !this.fields.username) || 
             !this.fields.name || 
             !this.fields.lastname || 
             (!this.isLimitedEdit && this.fields.roles.length < 1) || 
             (requiresVehicleCondition && !this.hasValidVehicleConditions)
             
      return disabled
    },
    searchTerm () {
      return this.userState.search
    },
    // Verificar si el usuario actual es admin
    isCurrentUserAdmin() {
      return this.CURRENT_USER_ROLES.includes('admin')
    },
    // Verificar si el usuario editado tiene el rol admin seleccionado
    editedUserIsAdmin() {
      return this.fields.roles.includes('admin') || this.fields.roles.includes('manager')
    },
    // Filtrar roles disponibles según el usuario actual
    availableRoles() {
      if (this.isAdminUser) {
        // Si es un usuario admin, mostrar todos los roles
        return this.roles.filter(role => role.value === 'admin')
      } else
      if (this.isCurrentUserAdmin) {
        // Si es admin, mostrar solo manager y admin
        return this.roles.filter(role => role.value === 'manager')
      } else {
        // Si no es admin, no puede asignar manager, administrador ni colaborador
        return this.roles.filter(role => role.value !== 'admin' && role.value !== 'colaborator')
      }
    }
  },

  watch: {
    searchTerm (val) {
      val && this.userDebouncedSearch()
    },
    visible (newVal) {
      if (newVal && this.isNewRecord) {
        this.resetFields()
      }
    },
    visiblePermissionsForm(newVal) {
      if (!newVal) {
        // Cuando se cierra el formulario de permisos, actualizar el modelo
        this.$nextTick(() => {
          this.fields.permissions = [...this.fields.permissions]
        })
      }
    },
    'fields.vehicleConditions': {
      handler() {
        // Forzar la reactividad
        this.$forceUpdate()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.user-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.87);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.section-title .v-icon {
  margin-right: 8px;
}

.roles-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.role-item {
  transition: background-color 0.2s ease;
}

.role-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.permissions-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.permissions-card .v-chip {
  font-size: 11px;
}
</style>
